import { Button } from "@/Button";
import { Dialog } from "@/Dialog";
import {
  openNativeLocationSettings,
  type DeviceLocationError,
} from "%/nativeLocation";

interface LocationPermissionDialogProps {
  error: DeviceLocationError | null;
  onClose: () => void;
}

const getCopy = (error: DeviceLocationError) => {
  switch (error) {
    case "PERMISSION_PERMANENTLY_DENIED":
      return {
        title: "위치 권한이 필요해요",
        message:
          "기기 설정에서 멍투데이의 위치 권한을 허용한 뒤 다시 시도해주세요.",
        settingsScope: "app" as const,
      };
    case "LOCATION_SERVICES_DISABLED":
      return {
        title: "위치 서비스가 꺼져 있어요",
        message:
          "기기의 위치 서비스를 켠 뒤 다시 시도해주세요.",
        settingsScope: "device" as const,
      };
    case "BROWSER_PERMISSION_DENIED":
      return {
        title: "브라우저 위치 권한이 필요해요",
        message:
          "주소창 왼쪽의 사이트 설정에서 위치 권한을 허용한 뒤 다시 시도해주세요.",
        settingsScope: null,
      };
    case "PERMISSION_DENIED":
      return {
        title: "위치 권한이 허용되지 않았어요",
        message:
          "내 위치 또는 거리순 버튼을 다시 누르면 위치 권한을 다시 요청할 수 있어요.",
        settingsScope: null,
      };
    case "TIMEOUT":
      return {
        title: "현재 위치를 확인하지 못했어요",
        message: "잠시 후 다시 시도해주세요.",
        settingsScope: null,
      };
    case "PERMISSION_REQUIRED":
      return {
        title: "위치 권한이 필요해요",
        message: "현재 위치를 사용하려면 위치 권한을 허용해주세요.",
        settingsScope: null,
      };
    default:
      return {
        title: "현재 위치를 확인하지 못했어요",
        message: "기기의 위치 상태를 확인한 뒤 다시 시도해주세요.",
        settingsScope: null,
      };
  }
};

export const LocationPermissionDialog = ({
  error,
  onClose,
}: LocationPermissionDialogProps) => {
  if (!error) return null;

  const copy = getCopy(error);
  const handleOpenSettings = () => {
    if (copy.settingsScope) {
      openNativeLocationSettings(copy.settingsScope);
    }
    onClose();
  };

  return (
    <Dialog visible onClose={onClose} persist={false}>
      <div className="flex flex-col gap-4 p-4 text-center">
        <div>
          <h3 className="mb-2 text-lg font-semibold">{copy.title}</h3>
          <p className="text-sm leading-6 text-gray">{copy.message}</p>
        </div>
        <div className="flex gap-3">
          {copy.settingsScope ? (
            <>
              <Button
                onClick={onClose}
                className="flex-1 rounded-full border border-[#E5E5E5] bg-white py-2 text-sm font-semibold text-gray"
              >
                취소
              </Button>
              <Button
                onClick={handleOpenSettings}
                className="flex-1 rounded-full bg-yellow py-2 text-sm font-semibold text-white"
              >
                설정으로 이동
              </Button>
            </>
          ) : (
            <Button
              onClick={onClose}
              className="w-full rounded-full bg-yellow py-2 text-sm font-semibold text-white"
            >
              확인
            </Button>
          )}
        </div>
      </div>
    </Dialog>
  );
};
